import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Content } from "antd/es/layout/layout";
import { Alert, Button, Form, Input, Modal, Skeleton } from "antd";
import {
  BankFilled,
  CheckCircleFilled,
  CloseOutlined,
  ExclamationCircleFilled,
  LeftOutlined,
  MailOutlined,
  SafetyCertificateOutlined,
  UserOutlined,
} from "@ant-design/icons";
import moment from "moment";
import axios from "axios";
import { generateClient } from "@aws-amplify/api";
import { useNavigate } from "react-router-dom";
import "./style.css";
import AppActionModal from "../Components/AppActionModal";
import DobInput from "../Components/DobInput";
import { getUser } from "../graphql/queries";
import { getTableID } from "../Services/authServices";

const API = generateClient();

const FINAL_APPROVED_STATUSES = ["APPROVED"];
const KYC_REVIEW_STATUSES = ["KYC_SUBMITTED"];
const KYB_REVIEW_STATUSES = ["KYB_SUBMITTED"];
const KYCUser = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [tableID, setTableID] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showPortalModal, setShowPortalModal] = useState(false);
  const [showPortalExitModal, setShowPortalExitModal] = useState(false);
  const [portalUrl, setPortalUrl] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [dbStatus, setDbStatus] = useState("");
  const [isOrganization, setIsOrganization] = useState(false);
  const [activeFlow, setActiveFlow] = useState("personal");
  const [errorMessage, setErrorMessage] = useState("");
  const [submissionNotice, setSubmissionNotice] = useState("");

  const loadUserState = useCallback(
    async (userTableID, { prefillForm = false } = {}) => {
      if (!userTableID) {
        throw new Error("Unable to load your account details.");
      }

      const { data } = await API.graphql({
        query: getUser,
        variables: { id: userTableID },
      });
      const userInfo = data?.getUser || {};

      setDbStatus(userInfo?.verificationStatus || "");
      setIsOrganization(!!userInfo?.isOrganization);
      setUserEmail(userInfo?.email || "");

      if (prefillForm) {
        form.setFieldsValue({
          firstName: userInfo?.firstName || "",
          lastName: userInfo?.lastName || "",
          dob: userInfo?.dateOfBirth ? moment(userInfo.dateOfBirth) : null,
          businessName: userInfo?.businessName || "",
          businessReferenceNumber: userInfo?.businessReferenceNumber || "",
        });
      }

      return userInfo;
    },
    [form],
  );

  useEffect(() => {
    const initializePage = async () => {
      try {
        setLoading(true);
        setErrorMessage("");

        const nextTableID = await getTableID();

        if (!nextTableID) {
          throw new Error("Unable to load your account details.");
        }
        setTableID(nextTableID);
        await loadUserState(nextTableID, { prefillForm: true });
      } catch (error) {
        console.error("Error initializing verification page:", error);
        setErrorMessage(
          error?.message ||
            "Unable to load your verification details right now.",
        );
      } finally {
        setLoading(false);
      }
    };

    initializePage();
  }, [loadUserState]);

  useEffect(() => {
    const requestCameraPermission = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
        });
        stream.getTracks().forEach((track) => track.stop());
      } catch {
        // Ignore until the user actually enters the portal.
      }
    };

    requestCameraPermission();
  }, []);

  const normalizedStatus = String(dbStatus || "PENDING").toUpperCase();
  const isPending = !normalizedStatus || normalizedStatus === "PENDING";
  const isRejected = normalizedStatus === "REJECTED";
  const isVerificationComplete =
    FINAL_APPROVED_STATUSES.includes(normalizedStatus);
  const isKycInitialized = normalizedStatus === "INITIALIZED";
  const isKycSubmitted = KYC_REVIEW_STATUSES.includes(normalizedStatus);
  const isKybInitialized = normalizedStatus === "KYB_INITIALIZED";
  const isKybSubmitted = KYB_REVIEW_STATUSES.includes(normalizedStatus);
  const lockPersonalFields =
    isKycInitialized ||
    isKycSubmitted ||
    isKybInitialized ||
    isKybSubmitted ||
    isVerificationComplete;
  const isStatusOnlyView =
    isKybSubmitted || isVerificationComplete || (isKycSubmitted && !isOrganization);
  const showBusinessFields = isOrganization;
  const showPersonalKycAction = true;
  const canStartPersonalKyc =
    isPending || isRejected || isKycInitialized;
  const canStartKyb =
    isOrganization && (isKycSubmitted || isKybInitialized);

  const verificationSummary = useMemo(() => {
    if (isVerificationComplete) {
      return {
        title: isOrganization
          ? "Verification complete"
          : "Identity verification complete",
        text: isOrganization
          ? "Your personal and business verification steps have been completed."
          : "Your identity verification has already been completed.",
      };
    }

    if (isRejected) {
      return {
        title: "Verification needs attention",
        text: isOrganization
          ? "Your previous verification attempt was not approved. Review your personal details and start verification again."
          : "Your previous verification attempt was not approved. Review your details and start again.",
      };
    }

    if (isOrganization) {
      if (isKybSubmitted) {
        return {
          title: "Business verification submitted",
          text: "Your business verification has been submitted and is under review.",
        };
      }

      if (isKycSubmitted || isKybInitialized) {
        return {
          title: "Continue business verification",
          text: isKybInitialized
            ? "Your personal KYC is complete. Continue your business verification to finish setup."
            : "Your personal KYC has been submitted. You can now start business verification.",
        };
      }

      if (isKycInitialized) {
        return {
          title: "Continue personal verification",
          text: "Your personal verification has been started. Continue with the same details to complete your KYC step.",
        };
      }

      return {
        title: "Complete verification in two steps",
        text: "Business accounts must complete personal KYC first, then business KYB.",
      };
    }

    if (isKycSubmitted) {
      return {
        title: "Verification submitted",
        text: "Your KYC submission is currently under review.",
      };
    }

    if (isKycInitialized) {
      return {
        title: "Continue identity verification",
        text: "Your identity verification has been started. Continue where you left off.",
      };
    }

    return {
      title: "Start identity verification",
      text: "Make sure your details match your government-issued ID exactly.",
    };
  }, [
    isKybInitialized,
    isKybSubmitted,
    isKycInitialized,
    isKycSubmitted,
    isOrganization,
    isRejected,
    isVerificationComplete,
  ]);

  const handleFormSubmit = async (values) => {
    form.setFieldsValue(values);
    if (activeFlow === "business") {
      handleConfirmSubmission();
      return;
    }
    setShowConfirmModal(true);
  };

  const handleStartFlow = async (flowType) => {
    setActiveFlow(flowType);
    try {
      await form.validateFields();
      const isResumeFlow =
        (flowType === "personal" && isKycInitialized) ||
        (flowType === "business" && isKybInitialized);

      if (isResumeFlow) {
        handleConfirmSubmission();
        return;
      }

      form.submit();
    } catch {
      // Ant Form handles field-level errors.
    }
  };

  const handleConfirmSubmission = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      setErrorMessage("");
      setShowConfirmModal(false);

      const currentTableID = tableID || (await getTableID());
      if (!currentTableID) {
        throw new Error("Table ID is not available.");
      }

      const formattedDob = values.dob ? values.dob.format("YYYY-MM-DD") : null;

      await API.graphql({
        query: `
          mutation UpdateUser($input: UpdateUserInput!) {
            updateUser(input: $input) { id }
          }
        `,
        variables: {
          input: {
            id: currentTableID,
            firstName: values.firstName,
            lastName: values.lastName,
            dateOfBirth: formattedDob,
            businessName:
              activeFlow === "business" ? values.businessName || "" : undefined,
            businessReferenceNumber:
              activeFlow === "business"
                ? values.businessReferenceNumber || ""
                : undefined,
          },
        },
      });

      const payload = {
        id: currentTableID,
        name: `${values.firstName} ${values.lastName}`,
        email: userEmail,
        nationality: "CAN",
        isOrganization: activeFlow === "business",
        type: activeFlow,
      };

      const response = await axios.post(
        "https://fmtcbjrxqc.execute-api.us-east-2.amazonaws.com/default/bajcan2debb57e-staging",
        payload,
      );

      const responseData =
        typeof response.data?.body === "string"
          ? JSON.parse(response.data.body)
          : response.data;

      if (!responseData?.portal_url) {
        throw new Error("Verification session could not be created.");
      }

      setPortalUrl(responseData.portal_url);
      setShowPortalModal(true);

    } catch (error) {
      console.error("Error starting verification flow:", error);
      setErrorMessage(
        error?.message ||
          "Unable to initialize the verification session right now.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDobValidityChange = (isValid) => {
    if (!isValid) {
      return;
    }

    if (form.getFieldError("dob").length > 0) {
      form.setFields([
        {
          name: "dob",
          errors: [],
        },
      ]);
    }
  };

  const handleTextFieldValidityChange = (fieldName, nextValue) => {
    if (!String(nextValue || "").trim()) {
      return;
    }

    if (form.getFieldError(fieldName).length > 0) {
      form.setFields([
        {
          name: fieldName,
          errors: [],
        },
      ]);
    }
  };

  const handlePortalCloseRequest = () => {
    setShowPortalExitModal(true);
  };

  const handlePortalNotCompleted = () => {
    setShowPortalModal(false);
    setShowPortalExitModal(false);
  };

  const handlePortalCompleted = () => {
    const nextStatus = activeFlow === "business" ? "KYB_SUBMITTED" : "KYC_SUBMITTED";
    setDbStatus(nextStatus);
    setSubmissionNotice(
      "A confirmation email has been sent to your registered email address. It may take a few seconds for your status to update. Do not try to start again immediately.",
    );
    setShowPortalModal(false);
    setShowPortalExitModal(false);
  };

  if (loading) {
    return (
      <Content className="appPageContent">
        <div className="appPageShell appPageShellNarrow">
          <div className="appTableCard kycCard">
            <Skeleton active paragraph={{ rows: 7 }} />
          </div>
        </div>
      </Content>
    );
  }

  return (
    <Content className="appPageContent">
      <div className="appPageShell appPageShellNarrow">
        <div className="appSectionHeader appPageHeader">
          <Button
            type="text"
            icon={<LeftOutlined />}
            className="backButton"
            onClick={() => navigate(-1)}
          />
          <h2 className="appSectionTitle">
            {isOrganization ? "Verification" : "KYC Verification"}
          </h2>
        </div>

        <div
          className={`appTableCard kycCard ${
            isStatusOnlyView ? "kycCardStatusOnly" : ""
          }`}
        >
          <div className={`kycHero ${isStatusOnlyView ? "kycHeroStatusOnly" : ""}`}>
            <div className="kycHeroBadge">
              {isVerificationComplete ? (
                <CheckCircleFilled />
              ) : (
                <SafetyCertificateOutlined />
              )}
            </div>
            <div className="kycHeroText">
              <h3>{verificationSummary.title}</h3>
              <p>{verificationSummary.text}</p>
            </div>
          </div>

          {errorMessage ? (
            <Alert
              type="error"
              showIcon
              message={errorMessage}
              className="kycStatusAlert"
            />
          ) : null}

          {submissionNotice ? (
            <div className="kycStatusNoteCard">
              <div className="kycStatusNoteIcon" aria-hidden="true">
                <CheckCircleFilled />
              </div>
              <div className="kycStatusNoteText">
                <p>{submissionNotice}</p>
              </div>
            </div>
          ) : null}

          {!isStatusOnlyView ? (
            <>
              {showPersonalKycAction ? (
                <div className="kycHelperCard">
                  <strong>
                    <MailOutlined style={{ marginRight: 8 }} />
                    Registered Email
                  </strong>
                  <p>
                    Use your registered email address whenever the verification portal
                    asks for contact details.
                  </p>
                  <div className="kycEmailValue">
                    {userEmail || "No email available"}
                  </div>
                </div>
              ) : null}

              <div className="kycFormWrap">
                <Form
                  form={form}
                  layout="vertical"
                  onFinish={handleFormSubmit}
                  requiredMark={false}
                  className="kycForm"
                  validateTrigger="onSubmit"
                >
                  <div className="kycFormGrid">
                    <Form.Item
                      name="firstName"
                      label="First Name"
                      rules={[
                        {
                          required: true,
                          message: "Please enter your first name.",
                        },
                      ]}
                    >
                      <Input
                        size="large"
                        placeholder="Enter first name"
                        disabled={lockPersonalFields}
                        onChange={(event) =>
                          handleTextFieldValidityChange(
                            "firstName",
                            event.target.value,
                          )
                        }
                      />
                    </Form.Item>

                    <Form.Item
                      name="lastName"
                      label="Last Name"
                      rules={[
                        {
                          required: true,
                          message: "Please enter your last name.",
                        },
                      ]}
                    >
                      <Input
                        size="large"
                        placeholder="Enter last name"
                        disabled={lockPersonalFields}
                        onChange={(event) =>
                          handleTextFieldValidityChange(
                            "lastName",
                            event.target.value,
                          )
                        }
                      />
                    </Form.Item>
                  </div>

                  <Form.Item
                    name="dob"
                    label="Date of Birth"
                    rules={[
                      {
                        validator: (_, value) => {
                          if (value && moment.isMoment(value) && value.isValid()) {
                            return Promise.resolve();
                          }

                          return Promise.reject(
                            new Error("Please enter a valid date of birth."),
                          );
                        },
                      },
                    ]}
                  >
                    <DobInput
                      disabled={lockPersonalFields}
                      onValidityChange={handleDobValidityChange}
                    />
                  </Form.Item>

                  {showBusinessFields ? (
                    <div className="kycFormGrid">
                      <Form.Item
                        name="businessName"
                        label="Business Name"
                        rules={[
                          {
                            required: true,
                            message: "Business name is required.",
                          },
                        ]}
                      >
                        <Input
                          size="large"
                          placeholder="Business name"
                          disabled={normalizedStatus !== "PENDING" && !isRejected}
                          onChange={(event) =>
                            handleTextFieldValidityChange(
                              "businessName",
                              event.target.value,
                            )
                          }
                        />
                      </Form.Item>

                      <Form.Item
                        name="businessReferenceNumber"
                        label="Business Reference Number"
                        rules={[
                          {
                            required: true,
                            message:
                              "Please enter the business reference number.",
                          },
                        ]}
                      >
                        <Input
                          size="large"
                          placeholder="Enter business reference number"
                          disabled={normalizedStatus !== "PENDING" && !isRejected}
                          onChange={(event) =>
                            handleTextFieldValidityChange(
                              "businessReferenceNumber",
                              event.target.value,
                            )
                          }
                        />
                      </Form.Item>
                    </div>
                  ) : null}
                </Form>
              </div>

              <div
                className={`kycFlowActions ${
                  !isOrganization || !showPersonalKycAction
                    ? "kycFlowActionsSingle"
                    : ""
                }`}
              >
                {showPersonalKycAction ? (
                  <div className="kycFlowActionCard">
                    <div className="kycFlowActionHeader">
                      <div className="kycFlowActionIcon">
                        <UserOutlined />
                      </div>
                      <div className="kycFlowActionText">
                        <h4>Personal KYC</h4>
                        <p>
                          Verify your personal identity using your legal name and date
                          of birth.
                        </p>
                      </div>
                    </div>
                    <Button
                      type="primary"
                      className="signupbtn kycFlowPrimaryBtn"
                      onClick={() => handleStartFlow("personal")}
                      loading={submitting && activeFlow === "personal"}
                      disabled={!canStartPersonalKyc}
                    >
                      {isKybInitialized || isKybSubmitted
                        ? "Personal KYC Complete"
                        : isKycSubmitted
                          ? "Personal KYC Submitted"
                          : isRejected
                            ? "Restart Personal KYC"
                            : isKycInitialized
                              ? "Continue Personal KYC"
                              : "Start Personal KYC"}
                    </Button>
                  </div>
                ) : null}

                {isOrganization ? (
                  <div
                    className={`kycFlowActionCard ${
                      !canStartKyb ? "kycFlowActionCardDisabled" : ""
                    }`}
                  >
                    <div className="kycFlowActionHeader">
                      <div className="kycFlowActionIcon">
                        <BankFilled />
                      </div>
                      <div className="kycFlowActionText">
                        <h4>Business KYB</h4>
                        <p>
                          Complete business verification after your personal KYC
                          step is submitted.
                        </p>
                      </div>
                    </div>
                    <Button
                      type={canStartKyb ? "primary" : "default"}
                      className={
                        canStartKyb
                          ? "signupbtn kycFlowPrimaryBtn"
                          : "appSecondaryBtn myAccountSecondaryBtn kycFlowSecondaryBtn"
                      }
                      onClick={() => handleStartFlow("business")}
                      loading={submitting && activeFlow === "business"}
                      disabled={!canStartKyb}
                    >
                      {isKybInitialized
                        ? "Continue Business KYB"
                        : "Start Business KYB"}
                    </Button>
                  </div>
                ) : null}
              </div>
            </>
          ) : null}
        </div>
      </div>

      <AppActionModal
        open={showConfirmModal}
        onCancel={() => setShowConfirmModal(false)}
        icon={<SafetyCertificateOutlined />}
        title="Confirm your information"
        description={
          activeFlow === "business"
            ? "Your personal details should match your legal documents before you continue to business verification."
            : "Your personal details should exactly match your ID before you continue."
        }
        secondaryAction={{
          label: "Edit",
          onClick: () => setShowConfirmModal(false),
          className: "myAccountSecondaryBtn",
        }}
        primaryAction={{
          label: "Confirm",
          onClick: handleConfirmSubmission,
          loading: submitting,
        }}
      />

      <Modal
        open={showPortalModal}
        onCancel={handlePortalCloseRequest}
        footer={null}
        centered
        width={900}
        className="kycIframeModal"
        destroyOnClose
        maskClosable={false}
        closable={false}
      >
        <div className="kycIframeLayout">
          <div className="kycIframeHeader">
            <div className="kycIframeHeaderText">
              <h3>
                {activeFlow === "business"
                  ? "Business Verification"
                  : "Identity Verification"}
              </h3>
              <p>Complete the secure verification session below.</p>
            </div>
            <Button
              type="text"
              className="kycIframeCloseBtn"
              icon={<CloseOutlined />}
              onClick={handlePortalCloseRequest}
              aria-label="Close verification window"
            />
          </div>
          <div className="kycIframeBody">
            {submitting ? (
              <div style={{ textAlign: "center", padding: "40px 0" }}>
                <Skeleton.Button active block style={{ height: 320 }} />
              </div>
            ) : (
              <iframe
                src={portalUrl}
                allow="camera; microphone; fullscreen"
                className="kycIframeEmbed"
                title="Verification Portal"
              />
            )}
          </div>
        </div>
      </Modal>

      <AppActionModal
        open={showPortalExitModal}
        onCancel={handlePortalNotCompleted}
        variant="warning"
        icon={<ExclamationCircleFilled />}
        title="Have you completed verification?"
        description="Select Yes only when you have completed all steps. Once submitted, you will not be able to start again."
        secondaryAction={{
          label: "No",
          onClick: handlePortalNotCompleted,
        }}
        primaryAction={{
          label: "Yes",
          onClick: handlePortalCompleted,
        }}
      />
    </Content>
  );
};

export default KYCUser;
